// src/services/zoomApiService.js
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, where, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import app, { db } from './firebase';

const functions = getFunctions(app);
const call = (name) => async (payload) => (await httpsCallable(functions, name)(payload)).data;

export const ZOOM_ROOM_DEFAULTS = {
  password: '123',
  joinBeforeHost: false,
  waitingRoom: false,
  autoRecording: 'cloud',
  muteUponEntry: true,
  hostVideo: true,
  participantVideo: false,
  topicMC: 'MASTERCLASS - {curso}',
  topicClase: '{curso}',
  autoCreateOnCourse: true,
  autoCreateOnDistribute: true,
  // Distribución: las clases pueden usar la sala de una MC de esta hora que ya terminó.
  reuseMcRooms: true,
  reuseMcHour: 17,
};

export const zoomErrorMessage = (err) => String(err?.message || err || 'Error desconocido').replace(/^.*?:\s*(?=[A-ZÁÉÍÓÚ"])/, '');

export const saveZoomConnection = call('zoomSaveConnection');
export const listZoomUsers = (connectionId) => call('zoomListUsers')({ connectionId: connectionId || null });
export const createZoomRoom = call('zoomCreateRoom');
export const createMissingZoomRooms = (items) => call('zoomCreateMissingRooms')({ items });
export const applyZoomSettings = (zoomAccountDocId) => call('zoomApplySettings')({ zoomAccountDocId });
export const syncZoomAccount =(zoomAccountDocId, apply) => call('zoomSyncAccount')({ zoomAccountDocId, ...(apply ? { apply } : {}) });

export const subscribeToZoomConnections = (workspaceId, cb) => {
  if (!workspaceId) { cb([]); return () => {}; }
  const q = query(collection(db, 'zoom_api_connections_public'), where('workspaceId', '==', workspaceId));
  return onSnapshot(q, s => cb(s.docs.map(d => ({ id: d.id, ...d.data() }))), (e) => { console.error('zoom connections:', e); cb([]); });
};

export const getZoomRoomSettings = async (workspaceId) => {
  const s = await getDoc(doc(db, 'zoom_room_settings', workspaceId));
  return { ...ZOOM_ROOM_DEFAULTS, ...(s.exists() ? s.data() : {}) };
};

export const saveZoomRoomSettings = async (workspaceId, settings) => {
  const clean = Object.fromEntries(Object.keys(ZOOM_ROOM_DEFAULTS).map(k => [k, settings[k] ?? ZOOM_ROOM_DEFAULTS[k]]));
  await setDoc(doc(db, 'zoom_room_settings', workspaceId), { ...clean, updatedAt: serverTimestamp() }, { merge: true });
};

export const listWorkspaceCourses = async (workspaceId) => {
  const s = await getDocs(query(collection(db, 'courses'), where('workspaceId', '==', workspaceId)));
  return s.docs
    .map(d => ({ id: d.id, name: d.data().name || d.data().title || '' }))
    .filter(c => c.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const normEmail = (s) => String(s || '').trim().toLowerCase();
export const routeAccountEmail = (email, publicConnections) => {
  const e = normEmail(email);
  if (!e) return null;
  for (const c of publicConnections || []) {
    const user = (c.users || []).find(u => normEmail(u.email) === e);
    if (user) return { connectionId: c.id, user };
  }
  return null;
};

// Crea las 2 salas (MC y Clase) de un curso nuevo. No bloquea ni lanza: el curso ya existe.
export const autoCreateZoomRoomsForCourse = async (workspaceId, courseId, courseName) => {
  try {
    if (!workspaceId || !courseId || !String(courseName || '').trim()) return;
    // Sin ninguna conexión Zoom API configurada: silencio total.
    const conns = await getDocs(query(collection(db, 'zoom_api_connections_public'), where('workspaceId', '==', workspaceId)));
    if (conns.empty) return;
    const s = await getZoomRoomSettings(workspaceId);
    if (!s.autoCreateOnCourse) return;
    const { results } = await createMissingZoomRooms([
      { courseId, courseName, kind: 'masterclass' },
      { courseId, courseName, kind: 'clase' },
    ]);
    const label = (k) => (k === 'masterclass' ? 'MC' : 'Clase');
    const ok = results.filter(r => r.ok);
    if (ok.length) toast.success(`Salas de Zoom listas: ${ok.map(r => `${label(r.kind)} en ${r.accountName}`).join(' · ')}`);
    results
      .filter(r => !r.ok && !/ninguna cuenta|conexión zoom api|administrador/i.test(String(r.error || '')))
      .forEach(r => toast.error(`Sala ${label(r.kind)} no creada: ${r.error}`, { duration: 7000 }));
  } catch (err) {
    // Sin conexión Zoom API o sin permisos: no molestar si nunca se configuró.
    const msg = zoomErrorMessage(err);
    if (!/ninguna cuenta|conexión zoom api|administrador/i.test(msg)) toast.error(`Salas de Zoom: ${msg}`, { duration: 7000 });
  }
};
